import { useState, useEffect, useRef } from 'react';
import { Check } from 'lucide-react';
import type { StepContentProps } from '~/ui-rocco/multi-step-form';
import { Input } from '@rocco/components/input';
import { StepContent } from '../StepContent';
import { HdeOptionList } from '../HDEOptionList';
import type { CurrentMemberFragment } from 'generated/graphql';
import {
    getHdeFieldLabelClassName,
    getHdeInputClassName,
    getHdeStepSectionClassName,
    getHdeStepTitleClassName,
} from '../hde-option-styles';
import { EMAIL_REGEX } from '@modules/validation/util';
import {
    getHdeFullNameValidationError,
    isHdeFullNameValid,
} from '~/modules/hde/utils/hde-name-validation';

interface GuestInvitationData {
    bringGuest: boolean | null;
    /** When true, user has clicked Next after selecting Yes – show guest details form */
    guestFormView?: boolean;
    guestName: string;
    guestEmail: string;
    sameContactDetails: boolean;
    user?: CurrentMemberFragment | null;
}

export function GuestInvitationStep({
    setIsValid,
    onDataChange,
    onNext,
    initialData,
}: StepContentProps) {
    const data = initialData as GuestInvitationData | undefined;
    const guestFormViewFromParent = data?.guestFormView ?? false;
    const [bringGuest, setBringGuest] = useState<boolean | null>(
        data?.bringGuest ?? null,
    );
    /** Local flag to show guest form instantly when user selects Yes (no Next click) */
    const [guestFormViewLocal, setGuestFormViewLocal] = useState(false);
    const lastSentBringGuestRef = useRef<boolean | null | undefined>(undefined);

    // Use parent value for display when parent has changed it (e.g. Back reset); otherwise use local state
    const parentHasOverridden =
        data?.bringGuest !== undefined &&
        data.bringGuest !== lastSentBringGuestRef.current;
    const displayBringGuest = parentHasOverridden
        ? data.bringGuest
        : bringGuest;

    // Sync bringGuest from parent when Back resets to question (e.g. from footer Back)
    useEffect(() => {
        setBringGuest(data?.bringGuest ?? null);
    }, [data?.bringGuest]);

    // Sync guestFormViewLocal: reset when parent explicitly sets guestFormView to false (Back button)
    useEffect(() => {
        if (guestFormViewFromParent === false) {
            setGuestFormViewLocal(false);
        }
    }, [guestFormViewFromParent]);

    /** Show guest form when: bringGuest is true AND (parent says show OR user just selected Yes locally) */
    const guestFormView = guestFormViewFromParent || guestFormViewLocal;
    const [guestName, setGuestName] = useState(data?.guestName ?? '');
    const [guestEmail, setGuestEmail] = useState(data?.guestEmail ?? '');

    const showGuestForm = bringGuest === true && guestFormView;
    const guestNameTrimmed = guestName.trim();
    const guestNameError =
        guestNameTrimmed.length > 0
            ? getHdeFullNameValidationError(guestName, { guest: true })
            : null;

    useEffect(() => {
        // When parent has overridden (e.g. Back reset), use parent value; otherwise use local state
        const effectiveBringGuest = parentHasOverridden
            ? data.bringGuest
            : bringGuest;
        lastSentBringGuestRef.current = effectiveBringGuest;

        // On question view: valid when user has made a selection
        // On guest form view: guest must have name (first + last) and valid email
        const isGuestEmailValid =
            guestEmail.trim().length > 0 && EMAIL_REGEX.test(guestEmail.trim());
        const isValid = !showGuestForm
            ? effectiveBringGuest !== null
            : guestNameTrimmed.length > 0 &&
              isGuestEmailValid &&
              isHdeFullNameValid(guestName);

        setIsValid(isValid);
        onDataChange?.({
            bringGuest: effectiveBringGuest,
            guestFormView: effectiveBringGuest ? guestFormView : false,
            guestName: effectiveBringGuest ? guestName : '',
            guestEmail: effectiveBringGuest ? guestEmail : '',
            sameContactDetails: false,
        });
    }, [
        bringGuest,
        data?.bringGuest,
        data?.guestFormView,
        guestFormView,
        guestName,
        guestNameTrimmed,
        guestEmail,
        showGuestForm,
        parentHasOverridden,
        setIsValid,
        onDataChange,
    ]);

    return (
        <div className="flex min-h-0 flex-1 flex-col gap-[38px]">
            <StepContent variant="guest">
                {!showGuestForm && (
                    <HdeOptionList
                        label="Are you bringing a guest?"
                        mode="single"
                        appearance="button"
                        options={[
                            {
                                value: 'true',
                                label: 'Yes, please register a guest',
                            },
                            {
                                value: 'false',
                                label: "No, I'll be attending by myself",
                            },
                        ]}
                        value={
                            displayBringGuest === null
                                ? null
                                : displayBringGuest
                                  ? 'true'
                                  : 'false'
                        }
                        onChange={(value) => {
                            const isYes = value === 'true';
                            setBringGuest(isYes);
                            if (isYes) {
                                setGuestFormViewLocal(true);
                            }
                        }}
                        onSelectAndAdvance={(value) => {
                            if (value === 'false') {
                                onDataChange?.({
                                    bringGuest: false,
                                    guestFormView: false,
                                    guestName: '',
                                    guestEmail: '',
                                    sameContactDetails: false,
                                });
                                onNext();
                            }
                        }}
                    />
                )}

                {showGuestForm && (
                    <div className={getHdeStepSectionClassName()}>
                        <label className={getHdeStepTitleClassName()}>
                            Are you bringing a guest?
                        </label>
                        <div className="flex w-full max-w-[465px] flex-col gap-7">
                            <div
                                className="flex min-h-[3rem] w-full items-center justify-between rounded-[0.7916rem] border-0 border-transparent bg-[#FDFDFD] px-[0.7916rem] py-[0.9676rem]"
                                aria-hidden
                            >
                                <span className="text-[0.9185rem] font-medium leading-[1.1] tracking-[-0.0184rem] text-[#2A2A2A]">
                                    Yes, please register a guest
                                </span>
                                <Check className="size-3.5 shrink-0 text-[#38383B]" />
                            </div>

                            <div className="relative flex flex-col gap-2">
                                <label className={getHdeFieldLabelClassName()}>
                                    Guest Name
                                </label>
                                <div className="relative">
                                    <Input
                                        type="text"
                                        value={guestName}
                                        onChange={(e) =>
                                            setGuestName(e.target.value)
                                        }
                                        placeholder="Full name"
                                        className={getHdeInputClassName()}
                                        aria-invalid={
                                            guestNameTrimmed.length > 0 &&
                                            guestNameError !== null
                                        }
                                        aria-describedby={
                                            guestNameTrimmed.length > 0 &&
                                            guestNameError
                                                ? 'guest-name-error'
                                                : undefined
                                        }
                                    />
                                    {guestNameTrimmed.length > 0 &&
                                        guestNameError && (
                                            <p
                                                id="guest-name-error"
                                                className="left-0 top-full mt-1 text-sm text-red-600"
                                                role="alert"
                                            >
                                                {guestNameError}
                                            </p>
                                        )}
                                </div>
                            </div>

                            <div className="relative flex flex-col gap-2">
                                <label className={getHdeFieldLabelClassName()}>
                                    Guest Email
                                </label>
                                <div className="relative">
                                    <Input
                                        type="email"
                                        value={guestEmail}
                                        onChange={(e) =>
                                            setGuestEmail(e.target.value)
                                        }
                                        placeholder="Email address"
                                        className={getHdeInputClassName()}
                                        aria-invalid={
                                            guestEmail.trim().length > 0 &&
                                            !EMAIL_REGEX.test(guestEmail.trim())
                                        }
                                        aria-describedby={
                                            guestEmail.trim().length > 0 &&
                                            !EMAIL_REGEX.test(guestEmail.trim())
                                                ? 'guest-email-error'
                                                : undefined
                                        }
                                    />
                                    {guestEmail.trim().length > 0 &&
                                        !EMAIL_REGEX.test(
                                            guestEmail.trim(),
                                        ) && (
                                            <p
                                                id="guest-email-error"
                                                className="left-0 top-full mt-1 text-sm text-red-600"
                                                role="alert"
                                            >
                                                Please enter a valid email
                                                address
                                            </p>
                                        )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </StepContent>
        </div>
    );
}
